<template>
  <div class="video-player-container" :class="{ 'blur-video': blur }">
    <video 
      ref="videoElement" 
      autoplay 
      playsinline 
      muted 
      class="video-element"
      @loadedmetadata="onVideoLoaded"
    ></video>
  </div>
</template>

<script>
export default {
  name: 'VideoPlayer',
  props: {
    videoTrack: {
      // Agora video track object (IAgoraRTCRemoteUser | ILocalVideoTrack) or null
      type: Object, 
      default: null,
    },
    blur: { // New prop to control blurring
      type: Boolean,
      default: false,
    }
  },
  watch: {
    videoTrack: {
      immediate: true, // Try playing immediately if track is already available
      handler(newTrack, oldTrack) {
        if (oldTrack && typeof oldTrack.stop === 'function') {
          try {
            oldTrack.stop();
            console.log('[VideoPlayer] Stopped previous video track.');
          } catch (e) {
            console.error('[VideoPlayer] Error stopping previous video track:', e);
          }
        }

        if (newTrack && this.$refs.videoElement) {
          console.log('[VideoPlayer] New video track received, attempting to play.');
          try {
            newTrack.play(this.$refs.videoElement);
            console.log(`VideoPlayer: Playing new track for UID: ${newTrack._originUid || 'local'}`);
          } catch (e) {
             console.error("Error playing video track:", e);
          }
        } else if (!newTrack && this.$refs.videoElement) {
          // Clear the video element if track is null
           const videoEl = this.$refs.videoElement;
           if (videoEl && videoEl.srcObject) {
              videoEl.srcObject.getTracks().forEach(track => track.stop());
              videoEl.srcObject = null;
              videoEl.load(); // Attempt to reset video element state
           }
           console.log("VideoPlayer: Track removed, clearing video element.");
        }
      },
    },
  },
  mounted() {
    // Attempt to play again if the track arrived before the ref was ready
    if (this.videoTrack && this.$refs.videoElement && !this.videoTrack.isPlaying) {
       console.log('[VideoPlayer] mounted - Attempting to play video track.');
       try {
         this.videoTrack.play(this.$refs.videoElement);
       } catch (e) {
         console.error('[VideoPlayer] mounted - Error playing video track:', e);
       }
    }
  },
  beforeDestroy() {
    // Stop the track when the component is destroyed
    if (this.videoTrack && typeof this.videoTrack.stop === 'function') {
      try {
        this.videoTrack.stop();
        console.log('[VideoPlayer] Stopped video track on component destroy.');
      } catch(e) {
         console.error('[VideoPlayer] Error stopping video track on destroy:', e);
      }
    }
  },
  methods: {
    onVideoLoaded(event) {
      console.log('Video metadata loaded:', event.target.videoWidth, 'x', event.target.videoHeight);
      // You could potentially adjust container size here if needed
    },
    /**
     * Captures the current frame of the video element as Base64.
     * @returns {Promise<string|null>} Base64 encoded image data URL or null on error/no video.
     */
    async captureFrame() {
      const videoElement = this.$refs.videoElement;
      if (!videoElement || videoElement.readyState < videoElement.HAVE_METADATA || videoElement.videoWidth === 0) {
        console.warn('Video element not ready or has no dimensions for capture.');
        return null;
      }

      try {
        const canvas = document.createElement('canvas');
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
        const context = canvas.getContext('2d');
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
        // Return as PNG - adjust type if needed (e.g., 'image/jpeg')
        return canvas.toDataURL('image/png'); 
      } catch (error) {
        console.error('Error capturing video frame:', error);
        return null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.video-player-container {
  width: 100%;
  height: 100%;
  overflow: hidden; // Prevent scrollbars if video overflows slightly
  background-color: #000; // Black background while loading/if no video
  position: relative; /* Needed for absolute positioning of video */

  .video-element {
    width: 100%;
    height: 100%;
    object-fit: cover; // Cover the area, cropping if needed
    display: block; /* Remove extra space below video */
    position: absolute;
    top: 0;
    left: 0;
  }

  /* Style for blurring */
  .blur-video .video-element {
    filter: blur(20px); /* Adjust blur amount as needed */
    transform: scale(1.1); /* Optional: scale slightly to avoid blurred edges */
  }
}
</style> 